import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "react-md";
import MGridForm from "components/Shared/MGridForm";
import { DATA_TYPE_DROPDOWN, DATA_TYPE_MULTI_LINE_STRING, DATA_TYPE_STRING } from "typeConstants/dataTypes";
import validator from "components/Shared/MGridFormDefinitions/validator";
import IncidentAttachments from "./IncidentAttachments";
import { CustomerServiceIncidentAttachmentMetadata, CustomerServiceIncidentAddFromCustomer } from "interfaces/Mueller.Business.Models";
import { insuranceReasonOptions } from "typeConstants/customerService";
import { addPendingIncidentAttachment, addIncidentFromCustomer } from "requests/customerService";

interface Props {
  controlIdNumber: number;
  onAfterSend: () => void;
};

const AddIncidentFromCustomer = ({
  controlIdNumber,
  onAfterSend,
} : Props) => {

  const mounted = useRef<boolean>(true);
  const formControlMethods = useRef<any>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [incident, setIncident] = useState<CustomerServiceIncidentAddFromCustomer>({
    controlIdNumber: controlIdNumber,
    reasonTypeCode: "",
    attachments: [],
    note: "",
    emailCc: "",
  });
  const [attachments, setAttachments] = useState<CustomerServiceIncidentAttachmentMetadata[]>([]);

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  const { getRootProps, isDragAccept } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: files => {      

      setIsUploading(true);
      files[0].arrayBuffer().then(ab => {
        
        addPendingIncidentAttachment(
          files[0].name, 
          ab, 
          (data) => {            
            const nextAttachments = attachments.slice(0);
            nextAttachments.push(data);
            onAttachmentsChanged(nextAttachments);
            setIsUploading(false);
          }, 
          () => {
            setIsUploading(false);
          }, 
          mounted
        );
      });
    },
  });
  const dropZoneBaseStyle = {
    border: "2px dashed transparent",
  };
  
  const dropZoneAcceptStyle = {
    border: "2px dashed red",
  };
  
  const activeStyle = useMemo(() => ({
    ...dropZoneBaseStyle,
    ...(isDragAccept ? dropZoneAcceptStyle : {}),
  }), [isDragAccept]);

  const formDefinition: any[] = [
    [
      { dataType: DATA_TYPE_STRING, label: "Email CC", source: "emailCc", hasValidationError: validator.semiColonSeparatedEmailAddresses }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Reason", source: "reasonTypeCode", dataTypeOptions: { options: insuranceReasonOptions }, hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_MULTI_LINE_STRING, label: "Note", source: "note", dataTypeOptions: { defaultLines: 5 }, hasValidationError: validator.required }
    ],
  ];

  const onAttachmentsChanged = (nextAttachments) => {
    setAttachments(nextAttachments);
  };

  const onFormChange = (updatedObject) => {
    setIncident({ ...updatedObject });
  };

  const onSubmit = () => {
    if (!formControlMethods.current.isFormValid()) return;

    incident.attachments = attachments.map(a => a.lookupKey);

    setIsSubmitting(true);
    addIncidentFromCustomer(
      incident,
      () => {
        setIsSubmitting(false);
        onAfterSend();
      },
      () => {
        setIsSubmitting(false);
      },
      mounted
    );
  };

  return (
    <div {...getRootProps({style: activeStyle})}>
      <MGridForm
        data={incident}
        onChange={onFormChange}
        formDefinition={formDefinition}
        getControlMethods={cm => { formControlMethods.current = cm; }}
        webform={true}
      />
      <IncidentAttachments
        attachments={attachments}
        onAttachmentsChanged={onAttachmentsChanged}
        disabled={isUploading}
      />
      <Button
        primary
        raised
        onClick={onSubmit}
        disabled={isSubmitting}>
        Send
      </Button>
    </div>
  );
};

export default AddIncidentFromCustomer;